<template>
  <div id="Post-results-grid-view">
    <ag-grid-vue
        ref="agGridTable"
        :gridOptions="gridOptions"
        :context="context"
        class="ag-theme-material w-100 my-4 ag-grid-table"
        :columnDefs="columnDefs"
        :defaultColDef="defaultColDef"
        :rowData="statsList"
        :pinnedBottomRowData="totalRow"
        rowSelection="multiple"
         :rowClassRules="rowClassRules"
        colResizeDefault="shift"
        :animateRows="true"
        :pagination="false"
        :suppressPaginationPanel="true"
        :enableRtl="$vs.rtl">
    </ag-grid-vue>
  </div>
</template>
<script>

import Vue from "vue";
import { AgGridVue } from "ag-grid-vue";
import "@/assets/scss/vuexy/extraComponents/agGridStyleOverride.scss";
import ElementEnum from '@/app/shared/ElementEnum';
import LFCellActions from './LocationForecastStatsCellActions.vue'
import LocationCellRenderer from './../../components/LocationCellRenderer.vue'
import ReceivedSampleRenderer from './ReceivedSampleRenderer.vue'
import * as postingStats from './../postingStats'


export default {
  name:"LocationStatsGrid",
  /* eslint-disable vue/no-unused-components */
  components: {
    AgGridVue,
    LFCellActions
  },
  data() {
    const gradeUnit = this.$t(this.$store.getters.isProjectImperial ? 'GradeControl.CappingUnitImperial' : 'GradeControl.CappingUnit');
    const gradeElements = [
      { element: ElementEnum.Gold, label: this.$t('Gold'), unit: gradeUnit, width: 130 },
      { element: ElementEnum.Silver, label: this.$t('Silver'), unit: gradeUnit, width: 130 },
      { element: ElementEnum.Copper, label: this.$t('Copper'), unit: '(%)', width: 130 },
      { element: ElementEnum.Zinc, label: this.$t('Zinc'), unit: '(%)', width: 120 },
      { element: ElementEnum.Lead, label: this.$t('Lead'), unit: '(%)', width: 120 },
      { element: ElementEnum.Carbon, label: this.$t('Carbon'), unit: '(%)', width: 130 },
      { element: ElementEnum.Sulfur, label: this.$t('Sulfur'), unit: '(%)', width: 130 }
    ];

    const gradeColumns = gradeElements.map(e => ({
      headerName: e.label + ' ' + e.unit,
      field: "results",
      width: e.width,
      valueFormatter: this.formatGrade,
      valueGetter: (params) => {
        const grade = this.getActualGrade(params.data, e.element);
        return grade === null ? null : this.$systemUnits.convertGrade(grade, e.element);
      }
    }));

    return {
      postedLocations: null,
      gradeElements,
      defaultColDef: {
        sortable: true,
        resizable: true
      },
      gridApi: null,
      gridOptions: {
        getRowNodeId: (data) => {
          return data.locationForecastId;
        }
      },
      rowClassRules: {
        'forecast-posted': 'data.isPosted'
      },
      context: { componentParent: this },
      columnDefs: [
        {
          headerName: '',
          field: 'locationForecastId',
          width: 50,
          editable: false,
          sortable: false,
          checkboxSelection: (params) => !params.node.rowPinned,
          headerCheckboxSelectionFilteredOnly: true,
          headerCheckboxSelection: true,
          cellRendererFramework: Vue.extend(LFCellActions)
        },
        {
          headerName: this.$t('GradeControl.Location.Name'),
          field: "locationCode",
          width: 170,
          filterParams: {
            valueGetter: params => {
              return params.data.locationCode;
            }
          },
          cellRendererFramework: Vue.extend(LocationCellRenderer),
          valueGetter: (params) => {
            const locationType = params.data.locationType;
            const locationCode = params.data.locationCode;
            const locations = this.$store.state.gradeControl.locations;
            return {
              locationType,
              locationCode,
              locations
            };
          },
          comparator:  (valueA, valueB) => {
            if (valueA.locationCode === valueB.locationCode) {
              return 0;
            }
            else {
              return (valueA.locationCode > valueB.locationCode) ? 1 : -1;
            }
          }
        },
        {
          headerName: this.$t('GradeControl.Sample.SampleReceivedAuRate'),
          field: "sampleReceivedAuRate",
          valueFormatter: this.formatPct,
          width: 180,
          cellRendererFramework: Vue.extend(ReceivedSampleRenderer),
          valueGetter: (params) => {
            const sampleRate = params.data.sampleReceivedAuRate;
            const receivedSampleCount = params.data.receivedAuSamples;
            const totalSampleCount = params.data.totalSamples;
            return {
              sampleRate,
              receivedSampleCount,
              totalSampleCount
            };
          },
          comparator:  (valueA, valueB) => {
            if (valueA.totalSampleCount === 0) {
              return -2;
            }
            if (valueA.sampleRate === valueB.sampleRate) {
              return 0;
            }
            else {
              return (valueA.sampleRate > valueB.sampleRate) ? 1 : -1;
            }
          }
        },
        {
          headerName: this.$t('GradeControl.Sample.SampleReceivedBaseRate'),
          field: "sampleReceivedBaseRate",
          valueFormatter: this.formatPct,
          width: 190,
          cellRendererFramework: Vue.extend(ReceivedSampleRenderer),
          valueGetter: (params) => {
            const sampleRate = params.data.sampleReceivedBaseRate;
            const receivedSampleCount = params.data.receivedBaseSamples;
            const totalSampleCount = params.data.totalSamples;
            return {
              sampleRate,
              receivedSampleCount,
              totalSampleCount
            };
          },
          comparator:  (valueA, valueB) => {
            if (valueA.totalSampleCount === 0) {
              return -2;
            }
            if (valueA.sampleRate === valueB.sampleRate) {
              return 0;
            }
            else {
              return (valueA.sampleRate > valueB.sampleRate) ? 1 : -1;
            }
          }
        },
        {
          headerName: this.$t('GradeControl.Tonnage') + ' ' + this.$t(this.$store.getters.isProjectImperial ? 'GradeControl.TonnageUnitImperial' : 'GradeControl.TonnageUnitMetric'),
          children: [
            {
              headerName: this.$t('GradeControl.Forecast.Name'),
              field: "forecastTonnage",
              valueFormatter: this.formatTonnage,
              valueGetter: (params) => this.$systemUnits.convertTonnage(this.getForecastTonnage(params.data)),
              width: 120
            },
            {
              headerName: this.$t('GradeControl.Actual'),
              field: "actualTonnage",
              valueFormatter: this.formatTonnage,
              valueGetter: (params) => this.$systemUnits.convertTonnage(this.getActualTonnage(params.data)),
              width: 120
            }
          ]
        },
        {
          // Display only : the density is shown but never frozen at posting (the snapshot holds
          // none, see PBI 25514). The total row carries its tonnage weighted average like the grades.
          headerName: this.$systemUnits.getDensityLabel(),
          field: "forecastDensity",
          valueFormatter: this.formatDensity,
          valueGetter: (params) => {
            const density = this.getDensity(params.data);
            return density === null ? null : this.$systemUnits.convertDensity(density);
          },
          width: 130
        },
        {
          headerName: "Grade",
          children: gradeColumns
        }
      ],
      components: {
        LFCellActions
      }
    }
  },
  computed: {
    statsList() {
      return this.$store.state.gradeControl.forecastStatsList;
    },
    // Pinned total row : tonnages are summed, grades and density are tonnage weighted averages,
    // same aggregation as the posting summary report.
    totalRow() {
      return postingStats.buildTotalRow(this.statsList, this.gradeElements.map(e => e.element), this.$t('GradeControl.Total'));
    }
  },
  methods: {
    formatGrade(params) {
      return this.$options.filters.toFriendlyGrade(params.value);
    },
    formatTonnage(params) {
      return this.$options.filters.toFriendlyTonnage(params.value);
    },
    formatDensity(params) {
      return this.$options.filters.toFriendlyGrade(params.value);
    },
    formatPct(params) {
      return this.$options.filters.toPercentage(params.value);
    },
    fetchStats(projectId, startDate, endDate) {
      return this.$store.dispatch("gradeControl/fetchPeriodForecastStats",  {
        startDate,
        endDate,
        projectId
      });
    },
    getActualGrade(data, elementId) {
      return postingStats.getActualGrade(data, elementId);
    },
    getDensity(data) {
      return postingStats.getDensity(data);
    },
    getActualTonnage(data) {
      return postingStats.getActualTonnage(data);
    },
    getForecastTonnage(data) {
      return postingStats.getForecastTonnage(data);
    },
    getSelectedUnpostedForecasts() {
      const rows = this.gridApi.getSelectedRows();
      return rows.filter(r => r.isPosted === false).map(l => l.locationForecastId);
    },
    getSelectedPostedForecastLocation() {
      const rows = this.gridApi.getSelectedRows();
      return rows.filter(r => r.isPosted === true).map(l => l.locationForecastId);
    },
    cancelPosting(locationForecast) {
      if (locationForecast) {
        this.$emit("onCancelPosting", locationForecast);
      }
    }
  },
  mounted() {
    this.gridApi = this.gridOptions.api;
  },
  created() {
    this.$vs.loading();
    Promise.all([this.$store.dispatch("gradeControl/fetchLocationListItems", Number(this.$store.state.projectId))]).finally(() => {
      this.$vs.loading.close();
    })
  }
};
</script>
<style lang="scss">
#Post-results-grid-view {
   .ag-header-group-text {
     text-align: center;
     border: 1px;
   }
   .ag-header-group-cell-label {
     align-content: center;
   }
   .ag-floating-bottom {
     font-weight: bold;
   }
}

.forecast-posted {
  border-width: 1px !important;
  border-style: solid !important;
  border-color:  #339900 !important;
}
</style>
